/**
 * OrderLink 日付計算ユーティリティ
 * - 既存の呼び出し元（引数省略）はすべてAsia/Tokyo基準のまま、一切動作を変えない
 * - 既存の請求書・issuedAt・注文ステータスには一切触れない
 *
 * 2026-08 AU版基盤（Phase 1）：全関数にtimezone引数を追加した。省略時のデフォルト値は
 * "Asia/Tokyo"のため、既存の日本版呼び出し元（App.js等、引数を渡していない箇所）は
 * このコミット以前と完全に同じ挙動になる。AU版はtenantドキュメントのtimezoneフィールド
 * （Phase 1ではデフォルト値"Australia/Sydney"を想定、実際の登録フローへの組み込みは
 * Phase 2）を呼び出し元から明示的に渡すことで、この共通関数群をそのまま再利用できる。
 */

const DEFAULT_TZ = "Asia/Tokyo";

/** Date → "YYYY-MM-DD"（指定タイムゾーン。省略時はAsia/Tokyo） */
export function toJSTDateString(date, timezone = DEFAULT_TZ) {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: timezone }).format(date);
}

// 2026-08 AU版基盤（Phase 1、Codexレビュー指摘対応・MUST-FIX→2ラウンド目MUST-FIX）：
// 任意タイムゾーンの "YYYY-MM-DDTHH:mm:ss" ローカル時刻に対応するUTC Dateを求める
// 汎用関数。単純な「日付差だけを見て日単位でシフトする」実装（1ラウンド目の反例：
// jstDateStringToDate("2026-07-20","Australia/Sydney")が実質2026-07-20T00:00:00Z
// ＝Sydney現地時間では同日10時や11時になってしまっていた）ではなく、候補UTC時刻と
// その地域のオフセット（時・分単位、DST込み）から収束計算する標準的な手法を使う。
//
// 2ラウンド目のCodexレビュー指摘：深夜0時台にDSTが切り替わる一部タイムゾーン
// （例：America/Santiago）では、その日の00:00:00という時刻自体が存在しない
// （spring-forward gap）場合や、DST終了で2回出現する場合がある。単純な2回収束
// ループだけでは、非存在時刻の間を振動して誤った時刻へ黙って収束してしまう
// リスクがあった。収束後に「候補をtimezoneで解釈した結果が目標と厳密に一致するか」
// を検証し、一致しない場合（非存在時刻＝spring-forward gap）は、そのgapの直後の
// 最初の実在時刻（=DSTで進んだ後の時刻）へ丸めるフォールバックを行う。
// なお、AU（Sydney/Melbourne/Adelaide/Brisbane/Perth等）はいずれも深夜0時台に
// DST切替が発生しないため、Phase 1のAU対応スコープでは00:00:00が非存在になる
// ケースは実際には発生しない。このフォールバックは関数契約としての堅牢性のために
// 用意する（他タイムゾーンへの将来拡張・堅牢性のため）
export function zonedTimeToUtc(y, m, d, hh, mm, ss, timezone) {
  const asIfUtc = Date.UTC(y, m - 1, d, hh, mm, ss);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const partsToUtcMs = (ms) => {
    const parts = fmt.formatToParts(new Date(ms));
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    // Intlのhour12:falseは24時を"24"として返すことがあるため0に正規化する
    const hour = get("hour") % 24;
    return Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  };
  let candidate = asIfUtc;
  let lastDiff = null;
  // 最大4回の補正で収束を試みる（通常1回、DST境界日でも2回で収束する）
  for (let i = 0; i < 4; i++) {
    const observedUtcForCandidate = partsToUtcMs(candidate);
    const diff = asIfUtc - observedUtcForCandidate;
    if (diff === 0) { lastDiff = 0; break; }
    // 前回と同じ補正量が繰り返される（振動）場合は、非存在時刻（spring-forward
    // gap）に入り込んでいると判断し、これ以上ループしても収束しない
    if (diff === lastDiff) break;
    lastDiff = diff;
    candidate += diff;
  }
  if (lastDiff !== 0) {
    // 非存在時刻（gap内）：候補時刻からUTC側で1分刻みに前進させ、
    // timezone側で観測される時刻が目標時刻以上になった最初の点
    // （＝DSTで進んだ直後の最初の実在時刻）へ丸める
    const GAP_SEARCH_STEP_MS = 60 * 1000;
    const GAP_SEARCH_MAX_STEPS = 240; // 最大4時間分（実在するDSTの切替幅を十分カバー）
    for (let i = 0; i < GAP_SEARCH_MAX_STEPS; i++) {
      const probe = candidate + i * GAP_SEARCH_STEP_MS;
      if (partsToUtcMs(probe) >= asIfUtc) {
        candidate = probe;
        break;
      }
    }
  }
  return new Date(candidate);
}

/** "YYYY-MM-DD" → その日の指定タイムゾーン 00:00:00 を表すDateオブジェクト。 */
export function jstDateStringToDate(str, timezone = DEFAULT_TZ) {
  const [y, m, d] = str.split("-").map(Number);
  if (timezone === DEFAULT_TZ) {
    // 既存の日本版経路：JSTはDSTを持たないためUTC+9固定のままでよい
    // （挙動を一切変えないための明示的な特別扱い。zonedTimeToUtcを通しても
    // 同じ結果になるが、既存の実装・既存テストの結果を1ビットも変えないため
    // あえて経路を分けている）
    return new Date(Date.UTC(y, m - 1, d) - 9 * 60 * 60 * 1000);
  }
  return zonedTimeToUtc(y, m, d, 0, 0, 0, timezone);
}

/**
 * 指定日（"YYYY-MM-DD"）が休日かどうか判定
 * @param {string} dateStr "YYYY-MM-DD"
 * @param {number[]} closedWeekdays 0=日曜〜6=土曜
 * @param {string[]} holidayDates ["YYYY-MM-DD", ...]
 * @param {string} timezone 省略時はAsia/Tokyo
 */
export function isHoliday(dateStr, closedWeekdays = [], holidayDates = [], timezone = DEFAULT_TZ) {
  const date = jstDateStringToDate(dateStr, timezone);
  const dowStr = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(date);
  const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dayOfWeek = dowMap[dowStr];
  if (closedWeekdays.includes(dayOfWeek)) return true;
  if (holidayDates.includes(dateStr)) return true;
  return false;
}

// 2026-08 AU版基盤（Phase 1、Codexレビュー指摘対応・2ラウンド目MUST-FIX）：
// "YYYY-MM-DD" のカレンダー上の日付を1日進める（タイムゾーン・時刻を一切
// 経由しない、純粋な暦計算）。修正前のnextBusinessDayはDateオブジェクトへ
// UTCミリ秒で24時間を加算していたため、DST終了日（例：Sydneyの2026-04-05、
// AEDTからAESTへ切り替わり、その日は25時間ある）をまたぐと、UTC24時間後が
// 同じカレンダー日のままになり、同じ日付を再度返してしまう不具合があった
// （Codex指摘）。日付の加算は必ずこの関数で行い、タイムゾーン変換は
// 加算後の年月日に対して都度おこなう
export function addCalendarDays(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  // UTC基準のカレンダー演算用Dateを使う（時刻・タイムゾーンは一切関与しない、
  // 純粋な年月日の繰り上がり計算のみ）
  const utcMidnight = new Date(Date.UTC(y, m - 1, d));
  utcMidnight.setUTCDate(utcMidnight.getUTCDate() + days);
  const yy = utcMidnight.getUTCFullYear();
  const mm = String(utcMidnight.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utcMidnight.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * dateStr の翌営業日を返す
 * @param {string} dateStr "YYYY-MM-DD"
 * @param {number[]} closedWeekdays
 * @param {string[]} holidayDates
 * @param {string} timezone 省略時はAsia/Tokyo
 * @returns {string} "YYYY-MM-DD"
 */
export function nextBusinessDay(dateStr, closedWeekdays = [], holidayDates = [], timezone = DEFAULT_TZ) {
  let str = dateStr;
  // 最大365日ループ（無限休日設定の安全弁）
  for (let i = 0; i < 365; i++) {
    str = addCalendarDays(str, 1);
    if (!isHoliday(str, closedWeekdays, holidayDates, timezone)) return str;
  }
  throw new Error("365日以内に営業日が見つかりません");
}

/**
 * 注文日時から「注文受付日」を計算（締め時間考慮）
 *
 * @param {Date|number} orderTimestamp 注文日時（Dateまたはミリ秒）
 * @param {string|null} cutoffTime "HH:MM" 形式、nullまたは未設定の場合は締め時間なし（常に当日）
 * @param {number[]} closedWeekdays
 * @param {string[]} holidayDates
 * @param {string} timezone 省略時はAsia/Tokyo
 * @returns {string} 注文受付日 "YYYY-MM-DD"
 */
export function calcOrderAcceptDate(orderTimestamp, cutoffTime, closedWeekdays = [], holidayDates = [], timezone = DEFAULT_TZ) {
  const date = orderTimestamp instanceof Date ? orderTimestamp : new Date(orderTimestamp);
  const todayJST = toJSTDateString(date, timezone);

  if (!cutoffTime) {
    // 締め時間未設定：常に当日受付
    // ただし当日が休日なら翌営業日
    if (isHoliday(todayJST, closedWeekdays, holidayDates, timezone)) {
      return nextBusinessDay(todayJST, closedWeekdays, holidayDates, timezone);
    }
    return todayJST;
  }

  // 注文時刻（指定タイムゾーン）のHH:MM を取得
  const jstTimeStr = new Intl.DateTimeFormat("ja-JP", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  // "14:30" 形式に正規化
  const orderHHMM = jstTimeStr.replace(/[^0-9:]/g, "").padStart(5, "0");

  // 締め時間ちょうどまで当日、超えたら翌営業日
  const isPastCutoff = orderHHMM > cutoffTime;

  let acceptDate;
  if (!isPastCutoff) {
    // 当日が休日でも、締め時間内なら翌営業日
    if (isHoliday(todayJST, closedWeekdays, holidayDates, timezone)) {
      acceptDate = nextBusinessDay(todayJST, closedWeekdays, holidayDates, timezone);
    } else {
      acceptDate = todayJST;
    }
  } else {
    // 締め時間超過：翌営業日を受付日とする
    acceptDate = nextBusinessDay(todayJST, closedWeekdays, holidayDates, timezone);
  }

  return acceptDate;
}

/**
 * 注文受付日からリード日数分営業日を進めて予定納品日を返す
 *
 * @param {string} acceptDateStr "YYYY-MM-DD" 注文受付日
 * @param {number} leadDays リード日数（0以上）
 * @param {number[]} closedWeekdays
 * @param {string[]} holidayDates
 * @param {string} timezone 省略時はAsia/Tokyo
 * @returns {string} 予定納品日 "YYYY-MM-DD"
 */
export function calcScheduledDeliveryDate(acceptDateStr, leadDays, closedWeekdays = [], holidayDates = [], timezone = DEFAULT_TZ) {
  if (leadDays < 0) leadDays = 0;
  let current = acceptDateStr;
  for (let i = 0; i < leadDays; i++) {
    current = nextBusinessDay(current, closedWeekdays, holidayDates, timezone);
  }
  // leadDays=0 の場合：受付日当日が予定納品日
  // 受付日は既にcalcOrderAcceptDateで営業日確認済みなのでそのまま返す
  return current;
}

/**
 * 注文日時・卸設定から予定納品日をまとめて計算するメイン関数
 *
 * @param {Date|number} orderTimestamp 注文日時
 * @param {object} wholesaleSettings { leadDays, cutoffTime, closedWeekdays, holidayDates, timezone }
 * @returns {string} 予定納品日 "YYYY-MM-DD"
 */
export function calcDeliveryDate(orderTimestamp, wholesaleSettings = {}) {
  const {
    leadDays = 0,
    cutoffTime = null,
    closedWeekdays = [],
    holidayDates = [],
    timezone = DEFAULT_TZ,
  } = wholesaleSettings;

  const acceptDate = calcOrderAcceptDate(orderTimestamp, cutoffTime, closedWeekdays, holidayDates, timezone);
  return calcScheduledDeliveryDate(acceptDate, leadDays, closedWeekdays, holidayDates, timezone);
}

/**
 * "YYYY-MM-DD" を日本語表示用に変換
 * @param {string|null} dateStr
 * @returns {string} "2026年7月24日" または "未設定"
 */
export function formatJPDate(dateStr) {
  if (!dateStr) return "未設定";
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${y}年${m}月${d}日`;
}

/**
 * "YYYY-MM-DD" をDD/MM/YYYY表示用に変換（AU版向け）。
 * 2026-08 AU版基盤（Phase 1）：formatJPDateとは別名の新規関数として追加。
 * 既存のformatJPDate呼び出し元には一切影響しない
 * @param {string|null} dateStr
 * @returns {string} "24/07/2026" または "Not set"
 */
export function formatAUDate(dateStr) {
  if (!dateStr) return "Not set";
  const [y, m, d] = dateStr.split("-").map(Number);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d)}/${pad(m)}/${y}`;
}

/**
 * marketIdに応じて日付表示をformatJPDate/formatAUDateへ振り分ける。
 * 2026-08 AU版基盤（Phase 1）：新規追加。marketId省略時はformatJPDateと
 * 同じ挙動（日本語表示）になるため、既存呼び出し元をformatDateForMarketへ
 * 置き換えても動作は変わらない（Phase 2以降で実際に置き換えるかは別途判断）
 * @param {string|null} dateStr
 * @param {string} marketId "JP" | "AU"
 */
export function formatDateForMarket(dateStr, marketId) {
  return marketId === "AU" ? formatAUDate(dateStr) : formatJPDate(dateStr);
}

/**
 * FirestoreのTimestampまたはDate/number から "YYYY-MM-DD"（指定タイムゾーン）を返す
 * @param {any} ts Firestoreタイムスタンプ or Date or number
 * @param {string} timezone 省略時はAsia/Tokyo
 * @returns {string|null}
 */
export function tsToJSTDateStr(ts, timezone = DEFAULT_TZ) {
  if (!ts) return null;
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  return toJSTDateString(date, timezone);
}
